import DOMPurify from "dompurify";
import { marked } from "marked";

const METRIC_CLASS_BY_TAG = {
  GOOD_UP: "metric-change--good-up",
  BAD_DOWN: "metric-change--bad-down",
  BAD_UP: "metric-change--bad-up",
  GOOD_DOWN: "metric-change--good-down",
};

const TABLE_HEADER_ICON = `
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events: none;">
    <circle cx="12" cy="12" r="1"></circle>
    <circle cx="12" cy="5" r="1"></circle>
    <circle cx="12" cy="19" r="1"></circle>
  </svg>
`;

const TABLE_HEADER = `
  <div class="table-wrapper-header">
    <span class="table-label"></span>
    <button class="table-options-trigger" aria-label="Table Options">
      ${TABLE_HEADER_ICON}
    </button>
  </div>
`;

const GROWTH_SIGN_PATTERN = "[+\\-\\u2212\\u2010-\\u2015\\ufe63\\uff0d\\uff0b]?";
const GROWTH_VALUE_PATTERN = `${GROWTH_SIGN_PATTERN}(?:\\d[\\d,]*)(?:\\.\\d+)?(?:[\\s\\u00a0\\u202f]*%)?`;
const GROWTH_TOKEN_REGEX = new RegExp(
  `\\[(GOOD_UP|BAD_DOWN|BAD_UP|GOOD_DOWN|NEUTRAL)\\]\\s*(${GROWTH_VALUE_PATTERN})`,
  "g"
);
const TAG_ONLY_REGEX = /^\s*\[(GOOD_UP|BAD_DOWN|BAD_UP|GOOD_DOWN|NEUTRAL)\]\s*$/;
const VALUE_ONLY_REGEX = new RegExp(`^${GROWTH_VALUE_PATTERN}$`);
const PERCENT_SPACING_REGEX = /[\s\u00a0\u202f]+%$/;
const UNICODE_MINUS_REGEX = /^[\u2212\u2010-\u2015\ufe63\uff0d]/;
const UNICODE_PLUS_REGEX = /^\uff0b/;

const injectTableWrappers = (html) =>
  html
    .replace(/<table>/g, `<div class="table-container">${TABLE_HEADER}<div class="table-scroll"><table>`)
    .replace(/<\/table>/g, "</table></div></div>");

const buildMetricNode = (doc, tag, value) => {
  const normalizedValue = normalizeMetricValue(value);

  if (tag === "NEUTRAL") {
    return doc.createTextNode(normalizedValue);
  }

  const wrapper = doc.createElement("span");
  wrapper.className = `metric-change ${METRIC_CLASS_BY_TAG[tag]}`;

  const ticker = doc.createElement("span");
  ticker.className = "metric-change__ticker";
  ticker.setAttribute("aria-hidden", "true");

  const label = doc.createElement("span");
  label.className = "metric-change__label";
  label.textContent = normalizedValue;

  wrapper.appendChild(ticker);
  wrapper.appendChild(label);
  return wrapper;
};

const normalizeMetricValue = (value) =>
  String(value || "")
    .trim()
    .replace(UNICODE_MINUS_REGEX, "-")
    .replace(UNICODE_PLUS_REGEX, "+")
    .replace(PERCENT_SPACING_REGEX, "%");

const replaceGrowthTokensInTextNode = (node, doc) => {
  const text = node.nodeValue || "";
  GROWTH_TOKEN_REGEX.lastIndex = 0;

  let match = null;
  let lastIndex = 0;
  let foundMatch = false;
  const fragment = doc.createDocumentFragment();

  while ((match = GROWTH_TOKEN_REGEX.exec(text)) !== null) {
    foundMatch = true;

    if (match.index > lastIndex) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
    }

    fragment.appendChild(buildMetricNode(doc, match[1], match[2]));
    lastIndex = match.index + match[0].length;
  }

  if (!foundMatch) {
    return;
  }

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  node.parentNode?.replaceChild(fragment, node);
};

const getNextRenderableSibling = (node) => {
  let sibling = node.nextSibling;

  while (sibling && sibling.nodeType === Node.TEXT_NODE && !String(sibling.nodeValue || "").trim()) {
    sibling = sibling.nextSibling;
  }

  return sibling;
};

const replaceSplitGrowthToken = (node, doc) => {
  const text = String(node.nodeValue || "");
  const tagMatch = text.match(TAG_ONLY_REGEX);

  if (!tagMatch) {
    return;
  }

  const sibling = getNextRenderableSibling(node);
  if (!sibling) {
    return;
  }

  const rawValue = String(sibling.textContent || "").trim();
  if (!VALUE_ONLY_REGEX.test(rawValue)) {
    return;
  }

  const normalizedValue = normalizeMetricValue(rawValue);

  if (tagMatch[1] === "NEUTRAL") {
    node.parentNode?.removeChild(node);
    if (sibling.nodeType === Node.TEXT_NODE) {
      sibling.nodeValue = normalizedValue;
    } else {
      sibling.textContent = normalizedValue;
    }
    return;
  }

  const wrapper = buildMetricNode(doc, tagMatch[1], normalizedValue);
  node.parentNode?.insertBefore(wrapper, node);
  node.parentNode?.removeChild(node);
  sibling.parentNode?.removeChild(sibling);
};

const transformGrowthTokens = (doc) => {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walker.nextNode();
  }

  textNodes.forEach((node) => {
    const parent = node.parentElement;
    if (!parent) return;
    if (parent.closest("code, pre, button, svg, script, style")) return;
    replaceGrowthTokensInTextNode(node, doc);
    replaceSplitGrowthToken(node, doc);
  });
};

export const renderAgentMarkdown = (text, { wrapTables = false } = {}) => {
  const markdown = String(text || "");
  const dirtyHtml = marked.parse(markdown, { breaks: true });
  const htmlWithTables = wrapTables ? injectTableWrappers(dirtyHtml) : dirtyHtml;
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlWithTables, "text/html");

  transformGrowthTokens(doc);

  return {
    __html: DOMPurify.sanitize(doc.body.innerHTML, {
      ADD_TAGS: ["button", "svg", "circle", "path"],
      ADD_ATTR: [
        "aria-hidden",
        "aria-label",
        "class",
        "cx",
        "cy",
        "fill",
        "height",
        "r",
        "stroke",
        "stroke-linecap",
        "stroke-linejoin",
        "stroke-width",
        "style",
        "viewBox",
        "width",
      ],
    }),
  };
};
